import { useState, useRef, useCallback, useMemo } from 'react'
import { Popover, Button, Body, useOnOutsideClick } from '@panwds/react-ui'
import { indexTsgTree } from '@sparky/framework'
import TSGTree from '../../../hub/components/common/TSGTree'
import { ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP } from '../../ErrorCodes'
import UInstanceTable from './UInstanceTable'

import type { VFC } from 'react'
import type { CommonStoreState } from '../../store'
import type { ReadonlyTsgTree } from '@sparky/framework'


type UTSGDropdownProps = {
  state: CommonStoreState
  selectedTsgId: string | undefined
  onChangeCallback: (tsgId: string) => void
}

const UTSGTreeDropdown: VFC<UTSGDropdownProps> = (props) => {
  const { state, selectedTsgId, onChangeCallback } = props
  const { tsgs, tsgDisplayType, apps, lockedInfo } = state
  let lockedTsgIds: string[] | undefined
  if (lockedInfo?.tsg_ids) {
    lockedTsgIds = lockedInfo.tsg_ids
  }
  const dropdownRef = useRef(null)
  const dropdownBtnRef = useRef(null)
  const [isDropdownOpen, setDropdownOpen] = useState<boolean>(false)
  const [showError] = useState<boolean>(false)
  const [hoveringTsgId, setHoveringTsgId] = useState<string>('')
  const [enableAddBtn, setEnableAddBtn] = useState<boolean>(true)
  const errorMessage = 'test'

  const filteredTsgs = useMemo<ReadonlyTsgTree>(() => {
    let newChildren = []
    // filter TSG passed to tsgTree based on display type
    switch (tsgDisplayType) {
      case 'default':
        setEnableAddBtn(true)
        return tsgs
      case 'absolute_root_with_children':
        setEnableAddBtn(true)
        newChildren = tsgs.children?.filter(each => each.isAbsoluteRoot) || []
        return indexTsgTree([{
          ...tsgs[0],
          children: newChildren
        }])
      case 'absolute_root_without_children':
        setEnableAddBtn(false)
        newChildren = tsgs.children?.filter(each => each.isAbsoluteRoot && !each.children) || []
        return indexTsgTree([{
          ...tsgs[0],
          children: newChildren
        }])
      default:
        return tsgs
    }
  }, [tsgs, tsgDisplayType])

  const selectedTsg = useMemo(() => {
    return selectedTsgId ? tsgs.get(selectedTsgId) : undefined
  }, [selectedTsgId, tsgs])

  const tsgDisplay = useMemo(() => {
    return selectedTsg ? selectedTsg.display_name : 'Select Tenant'
  }, [selectedTsg])

  const onButtonClick = () => {
    setDropdownOpen((preveState: boolean) => {
      return !preveState
    })
  }
  useOnOutsideClick([dropdownBtnRef, dropdownRef], () => {
    setDropdownOpen(false)
  })

  const onTsgSelect = useCallback((tsgId: string) => {
    onChangeCallback(tsgId)
    setDropdownOpen(false)
  }, [onChangeCallback])

  const instanceTable = useMemo(() => {
    if (hoveringTsgId) {
      return <UInstanceTable id={hoveringTsgId} apps={apps} />
    }
    else {
      return null
    }
  }, [apps, hoveringTsgId])

  return <div className='cm-w-fit' ref={dropdownBtnRef}>
    <Popover
      button={<Button
        appearance='tertiary'
        isMenu
        isOpen={isDropdownOpen}
        onClick={onButtonClick}
      >
        <span className='cm-w-64 cm-text-left'>{tsgDisplay}</span>
      </Button>}
      label='tsg-selection'
      showLabel={false}
      defaultPlacement='bottom-start'
      isOpen={isDropdownOpen}
      onIsOpenChange={(next: boolean) => setDropdownOpen(next)}
    >
      <div
        className='cm-w-72 cm-relative'
        ref={dropdownRef}
      >
        <div className='cm-w-full'>
          <TSGTree
            addTreeClassName='cm-max-h-96'
            tsgs={filteredTsgs}
            tsg={selectedTsg}
            onSelect={onTsgSelect}
            onClickAddBtn={() => setDropdownOpen(false)}
            selectedTsgId={selectedTsgId}
            isVisible={isDropdownOpen}
            hasAddBtn={enableAddBtn}
            rootAddBtn
            showSuperRoot
            hoverToShowInstanceTable
            setHoveringTsgId={setHoveringTsgId}
            lockedTsgIds={lockedTsgIds}
          />
        </div>
        {hoveringTsgId && <div className='cm-absolute cm-left-72 cm-top-0 cm-ml-1'>
          {instanceTable}
        </div>}
      </div>
    </Popover>
    {showError && <Body size="sm" appearance={'error'} addClassName='tw-items-center tw-py-1.5 tw-pl-1'>
      {ACTIVATION_ERROR_CODE_TO_DISPLAY_MSG_MAP[errorMessage]}</Body>}
  </div>
}
export default UTSGTreeDropdown
